/**
 * SPCB "Other" Shipping Modal Module v1.10.0
 *
 * Handles:
 * - "Otro" (Other transport) modal creation and management
 * - Transport name input
 * - Data persistence (localStorage, cookie)
 * - Summary display
 *
 * @package SuperPymeCheckoutBlocks
 */

// Import shared utilities from parent module
const getLog = () => window.spcbShippingUtils?.log || ((...args) => {});
const getUserClickedShipping = () => window.spcbShippingUtils?.userClickedShipping || false;
const setUserClickedShipping = (val) => {
    if (window.spcbShippingUtils) window.spcbShippingUtils.userClickedShipping = val;
};

// Local helper to get the selected shipping label (previously from branch-modal)
function getSelectedShippingLabel() {
    const selectedRadio = document.querySelector('.wc-block-components-shipping-rates-control input[type="radio"]:checked');
    if (!selectedRadio) return null;
    return selectedRadio.closest('label');
}

let otherModalCreated = false;

// ============================================
// DETECTION HELPERS
// ============================================

export function isOtherShippingSelected() {
    const selectedRadio = document.querySelector('.wc-block-components-shipping-rates-control input[type="radio"]:checked');
    if (!selectedRadio) return false;

    const methodId = selectedRadio.value || '';
    if (methodId.startsWith('spcb_other_shipping')) return true;

    const label = selectedRadio.closest('label');
    const labelText = label?.querySelector('.wc-block-components-radio-control__label')?.textContent?.toLowerCase() || '';
    return labelText.includes('otro');
}

// ============================================
// DATA PERSISTENCE
// ============================================

export function getOtherData() {
    try {
        const saved = localStorage.getItem('spcb_other_data');
        if (saved) {
            return JSON.parse(saved);
        }

        const cookie = document.cookie.split('; ').find(row => row.startsWith('spcb_other_data='));
        if (cookie) {
            return JSON.parse(decodeURIComponent(cookie.split('=')[1]));
        }
    } catch (e) {
        getLog()('Could not read other data:', e);
    }
    return null;
}

export function saveOtherDataToCookie(data) {
    const cookieValue = encodeURIComponent(JSON.stringify(data));
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + (60 * 60 * 1000)); // 1 hour
    document.cookie = `spcb_other_data=${cookieValue}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
}

export function updateOtherHiddenField(data) {
    let hiddenField = document.querySelector('input[data-spcb-field="other_data"]');
    if (!hiddenField) {
        hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.setAttribute('data-spcb-field', 'other_data');
        hiddenField.name = 'spcb_other_data';
        const form = document.querySelector('form.wc-block-checkout__form');
        if (form) {
            form.appendChild(hiddenField);
        }
    }
    hiddenField.value = JSON.stringify(data);
    // v1.15.2: Guard to prevent dispatch during checkout processing
    if (!window.spcbCheckoutLock?.isProcessing?.()) {
        hiddenField.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

// ============================================
// MODAL CREATION
// ============================================

export function createOtherModal() {
    if (otherModalCreated) return;

    const config = window.spcbConfig || {};
    const message = config.otherShippingMessage || 'Comunicarse con el transporte para coordinar el retiro en el local.';
    const phone = config.otherShippingPhone || '+56 9 4243 0777';

    const formattedMessage = message.split('\n').map(line => `<p style="margin: 0 0 8px 0;">${line}</p>`).join('');
    const whatsappPhone = phone.replace(/[\s-]/g, '').replace(/^\+/, '');
    const whatsappLink = `https://wa.me/${whatsappPhone}`;

    const modalHTML = `
        <div id="spcb-other-modal" class="spcb-modal spcb-other-modal" style="display:none;">
            <div class="spcb-modal-overlay"></div>
            <div class="spcb-modal-content">
                <div class="spcb-modal-header">
                    <h3>Envío con Transporte Propio</h3>
                    <button type="button" class="spcb-modal-close" aria-label="Cerrar">&times;</button>
                </div>
                <div class="spcb-modal-body">
                    <div class="spcb-other-info-box">
                        <div class="spcb-other-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div class="spcb-other-message">
                            ${formattedMessage}
                        </div>
                    </div>

                    <div class="spcb-other-phone">
                        <a href="${whatsappLink}" target="_blank" rel="noopener" class="spcb-whatsapp-link">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            <span>${phone}</span>
                        </a>
                    </div>

                    <div class="spcb-modal-field">
                        <label for="spcb-other-transport">Nombre del transporte <span class="required">*</span></label>
                        <input type="text" id="spcb-other-transport" placeholder="Ej: Turbus Cargo, Pullman Bus" required>
                        <p class="spcb-field-hint">Ingresa el nombre del transporte que enviará a retirar tu pedido.</p>
                    </div>
                </div>
                <div class="spcb-modal-footer">
                    <button type="button" class="spcb-modal-cancel">Cancelar</button>
                    <button type="button" class="spcb-modal-confirm">Confirmar</button>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    otherModalCreated = true;

    setupOtherModalEvents();
    getLog()('Other shipping modal created');
}

function setupOtherModalEvents() {
    const modal = document.getElementById('spcb-other-modal');
    if (!modal) return;

    modal.querySelector('.spcb-modal-close').addEventListener('click', () => closeOtherModal(false));
    // NO cerrar con click en overlay - solo con X o botones
    modal.querySelector('.spcb-modal-cancel').addEventListener('click', () => closeOtherModal(false));
    modal.querySelector('.spcb-modal-confirm').addEventListener('click', confirmOtherData);

    modal.querySelector('#spcb-other-transport').addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            confirmOtherData();
        }
    });
}

// ============================================
// MODAL OPEN/CLOSE
// ============================================

export function openOtherModal() {
    createOtherModal();

    const modal = document.getElementById('spcb-other-modal');
    if (!modal) return;

    const existingData = getOtherData();
    if (existingData && existingData.transportName) {
        document.getElementById('spcb-other-transport').value = existingData.transportName;
    }

    modal.querySelectorAll('.spcb-field-error').forEach(el => el.classList.remove('spcb-field-error'));

    const warningEl = modal.querySelector('.spcb-validation-warning');
    if (warningEl) warningEl.style.display = 'none';

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Solo autofocus en desktop - en móvil el teclado tapa el mensaje informativo
    if (window.innerWidth > 600) {
        setTimeout(() => {
            document.getElementById('spcb-other-transport')?.focus();
        }, 100);
    }

    getLog()('Other shipping modal opened');
}

export function closeOtherModal(confirmed) {
    const modal = document.getElementById('spcb-other-modal');
    if (modal) {
        modal.style.display = 'none';
        document.body.style.overflow = '';
    }

    if (!confirmed) {
        setUserClickedShipping(false);
        getLog()('Reset userClickedShipping to prevent modal cascade');

        const existingData = getOtherData();
        if (!existingData || !existingData.transportName) {
            // REMOVED in v1.10.17: selectAlternativeShippingMethod() was causing interference
            // with WooCommerce Blocks' native delivery method selection (Enviar vs Retiro).
            // Now we just show a prompt and let the user choose.
            if (window.spcbShippingUtils?.showShippingPrompt) {
                window.spcbShippingUtils.showShippingPrompt('other');
            }
            removeOtherSummary();
        }
    }

    getLog()('Other shipping modal closed, confirmed:', confirmed);
}

function confirmOtherData() {
    const transportInput = document.getElementById('spcb-other-transport');
    const transportName = transportInput?.value?.trim() || '';

    if (!transportName) {
        transportInput?.classList.add('spcb-field-error');
        transportInput?.focus();
        return;
    }

    transportInput?.classList.remove('spcb-field-error');

    const data = {
        transportName: transportName
    };

    try {
        localStorage.setItem('spcb_other_data', JSON.stringify(data));
    } catch (e) {
        getLog()('Could not save to localStorage:', e);
    }

    saveOtherDataToCookie(data);
    updateOtherHiddenField(data);
    showOtherSummary(data);
    closeOtherModal(true);

    getLog()('Other shipping data confirmed:', data);
}

// ============================================
// OTHER SUMMARY
// ============================================

export function showOtherSummary(data) {
    const shippingLabel = getSelectedShippingLabel();
    if (!shippingLabel) return;

    removeOtherSummary();

    const optionLayout = shippingLabel.querySelector('.wc-block-components-radio-control__option-layout');
    if (!optionLayout) return;

    const summary = document.createElement('div');
    summary.className = 'spcb-other-summary spcb-branch-summary';
    summary.innerHTML = `
        <div class="spcb-branch-info">
            <span class="spcb-branch-icon">🚛</span>
            <span class="spcb-branch-details">
                Transporte: <strong>${data.transportName}</strong>
            </span>
        </div>
        <button type="button" class="spcb-branch-edit" aria-label="Editar transporte">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Editar
        </button>
    `;

    summary.querySelector('.spcb-branch-edit').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openOtherModal();
    });

    optionLayout.appendChild(summary);

    getLog()('Other shipping summary shown');
}

export function removeOtherSummary() {
    document.querySelectorAll('.spcb-other-summary').forEach(el => el.remove());
}

// Initialize module marker
export const MODULE_NAME = 'other-modal';
export const MODULE_VERSION = '1.10.0';
